import {
  DXF_ENTITY_TYPE,
  DXF_PMI_ENTITY_TYPES,
  type DxfEntity,
  type DxfScene,
  type DxfText,
} from '@frontend/api';
import {
  type Aabb2,
  type Point2,
  type PolylineInput,
  type SnapCandidate,
  buildLoopIndex,
  buildSnapIndex,
  flattenedCurveCenter,
  flattenedShapeCenter,
  polylineBounds,
  stitchLoops,
  unionAabb,
} from '@frontend/viewer-core';

import type { DrawingModel, ModelText } from './types';

/**
 * The stable address of the *source entity* a primitive came from.
 *
 * `h` is the top-level handle, shared by every primitive one INSERT or DIMENSION
 * exploded into; `src` is the block-definition chain that disambiguates within it.
 * Together they are what the vocabulary calls an Entity — the unit roles are keyed
 * by, and the unit a layer's count should report.
 */
export const entityKey = (entity: Pick<DxfEntity, 'h' | 'src'>): string =>
  entity.src?.length ? `${entity.h}/${entity.src.join('/')}` : entity.h;

/**
 * Content fingerprint for a scene text row — source entity, pose, and string.
 *
 * Not unique on its own: identical labels stacked on the same pose share a base
 * key. `deriveModel` disambiguates collisions with an occurrence suffix.
 */
export const textKey = (text: DxfText): string => {
  const [x, y] = text.pos;
  return `${entityKey(text)}|${x},${y}|${text.width}x${text.height}|${
    text.rotation
  }|${text.s}`;
};

/**
 * Assigns ids unique within one derivation, appending `#n` when `textKey` collides.
 *
 * The first row keeps the bare fingerprint; later collisions become `#1`, `#2`, …
 * so React keys and future addressing never invent an index at render time.
 */
const assignTextIds = (sceneTexts: readonly DxfText[]): ModelText[] => {
  const seen = new Map<string, number>();
  return sceneTexts.map((text) => {
    const base = textKey(text);
    const occurrence = seen.get(base) ?? 0;
    seen.set(base, occurrence + 1);
    return {
      id: occurrence === 0 ? base : `${base}#${occurrence}`,
      text,
    };
  });
};

/**
 * Fraction of the drawing's diagonal within which two endpoints are the same point.
 *
 * Relative rather than absolute because a drawing may be in millimetres or in
 * inches, and a fixed epsilon would either weld nothing in one or weld distinct
 * features together in the other. A millionth of the diagonal is far below any
 * feature a person drew and far above the rounding a CAD exporter leaves behind.
 */
const STITCH_TOLERANCE_FRACTION = 1e-6;

/** Circular geometry: three points on it determine its centre exactly. */
const CIRCULAR_ENTITY_TYPES: ReadonlySet<string> = new Set([
  DXF_ENTITY_TYPE.CIRCLE,
  DXF_ENTITY_TYPE.ARC,
]);

/**
 * Non-circular curves whose centre is the middle of their extent.
 *
 * An ellipse has to be handled this way: three points on one do not determine
 * its centre, and running a circumcenter over them lands somewhere arbitrary.
 */
const EXTENT_CENTER_ENTITY_TYPES: ReadonlySet<string> = new Set([
  DXF_ENTITY_TYPE.ELLIPSE,
]);

/**
 * Snap-grid cell size, as a fraction of the drawing's diagonal.
 *
 * Set from the drawing rather than from the pick radius: the radius changes with
 * zoom on every query, and rebuilding the grid per pointer move would cost far
 * more than scanning a few extra cells.
 */
const SNAP_CELL_FRACTION = 1 / 128;

const stitchToleranceFor = (bounds: Aabb2 | null): number => {
  if (!bounds) {
    return STITCH_TOLERANCE_FRACTION;
  }
  const diagonal = Math.hypot(bounds[2] - bounds[0], bounds[3] - bounds[1]);
  return diagonal > 0
    ? diagonal * STITCH_TOLERANCE_FRACTION
    : STITCH_TOLERANCE_FRACTION;
};

/**
 * Endpoints, midpoints and — for a flattened curve — the centre.
 *
 * Midpoints are per *entity* rather than per tessellated segment: a flattened arc
 * has dozens of segments and offering a snap at each of their midpoints would bury
 * the targets a person actually wants under noise.
 */
const collectSnapCandidates = (
  entity: DxfEntity,
  into: SnapCandidate[],
): void => {
  const points = entity.pts;
  if (points.length === 0 || entity.fill) {
    return;
  }

  const first = points[0] as Point2;
  const last = points[points.length - 1] as Point2;
  into.push({ point: first, kind: 'endpoint', layer: entity.layer });
  if (points.length > 1) {
    into.push({ point: last, kind: 'endpoint', layer: entity.layer });
  }

  if (
    CIRCULAR_ENTITY_TYPES.has(entity.t) ||
    EXTENT_CENTER_ENTITY_TYPES.has(entity.t)
  ) {
    const center = CIRCULAR_ENTITY_TYPES.has(entity.t)
      ? flattenedCurveCenter(points as Point2[])
      : flattenedShapeCenter(points as Point2[]);
    if (center) {
      into.push({ point: center, kind: 'center', layer: entity.layer });
    }
    return;
  }

  if (points.length === 2) {
    into.push({
      point: [(first[0] + last[0]) / 2, (first[1] + last[1]) / 2],
      kind: 'midpoint',
      layer: entity.layer,
    });
    return;
  }

  // A polyline's vertices are its corners; each straight run between them gets a
  // midpoint, which is what a user reaches for on a rectangle or a slot.
  for (let index = 1; index < points.length; index += 1) {
    const a = points[index - 1] as Point2;
    const b = points[index] as Point2;
    into.push({
      point: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
      kind: 'midpoint',
      layer: entity.layer,
    });
  }
};

/**
 * Reduces a scene to the model the viewer works against.
 *
 * One pass over the entities produces every aggregate, because the drawings that
 * matter here run to tens of thousands of primitives and this runs synchronously
 * when a scene lands.
 *
 * `scene.bbox` is preferred for the overall bounds when the backend supplies it,
 * but per-layer bounds have to be measured either way — visibility-aware fitting
 * needs them, and the wire format carries no per-layer extents.
 */
export const deriveModel = (scene: DxfScene): DrawingModel => {
  const boundsByLayer = new Map<number, Aabb2>();
  const entityKeysByLayer = new Map<number, Set<string>>();
  const layersWithPmi = new Set<number>();
  const stitchable: PolylineInput[] = [];
  const snapCandidates: SnapCandidate[] = [];
  /** Source-entity keys whose geometry is dimension annotation. */
  const pmiEntityKeys = new Set<string>();

  scene.entities.forEach((entity) => {
    if (entity.pts.length === 0) {
      return;
    }

    // Fills are regions, not paths: a hatch has no meaningful chain to stitch into
    // and would drag a loop's outline through its interior. Its bounds and its
    // entity count still count, so it is excluded here rather than skipped above.
    if (!entity.fill && entity.pts.length >= 2) {
      stitchable.push({
        entityId: entityKey(entity),
        layer: entity.layer,
        points: entity.pts,
      });
    }

    // Holes sit inside their ring by construction, so they cannot widen bounds.
    const measured = polylineBounds(entity.pts);
    if (measured) {
      const merged = unionAabb(boundsByLayer.get(entity.layer), measured);
      if (merged) {
        boundsByLayer.set(entity.layer, merged);
      }
    }

    let keys = entityKeysByLayer.get(entity.layer);
    if (!keys) {
      keys = new Set();
      entityKeysByLayer.set(entity.layer, keys);
    }
    keys.add(entityKey(entity));

    if (DXF_PMI_ENTITY_TYPES.has(entity.t)) {
      layersWithPmi.add(entity.layer);
      pmiEntityKeys.add(entityKey(entity));
    }

    collectSnapCandidates(entity, snapCandidates);
  });

  // Text is a real entity on its layer even though it carries no geometry, so it
  // counts — but its box is placed by the text layer, not measured here.
  const texts = assignTextIds(scene.texts);
  texts.forEach(({ text }) => {
    let keys = entityKeysByLayer.get(text.layer);
    if (!keys) {
      keys = new Set();
      entityKeysByLayer.set(text.layer, keys);
    }
    keys.add(entityKey(text));
  });

  const measuredBounds = [...boundsByLayer.values()].reduce<Aabb2 | null>(
    (accumulated, layerBounds) => unionAabb(accumulated, layerBounds),
    null,
  );

  const entityCountsByLayer = new Map<number, number>();
  entityKeysByLayer.forEach((keys, layerIndex) => {
    entityCountsByLayer.set(layerIndex, keys.size);
  });

  const bounds = scene.bbox ?? measuredBounds;
  const loops = stitchLoops(stitchable, {
    tolerance: stitchToleranceFor(bounds),
  });

  const diagonal = bounds
    ? Math.hypot(bounds[2] - bounds[0], bounds[3] - bounds[1])
    : 0;

  return {
    scene,
    texts,
    bounds,
    boundsByLayer,
    entityCountsByLayer,
    layersWithPmi,
    pmiEntityKeys,
    hasPmi: pmiEntityKeys.size > 0,
    loops,
    index: buildLoopIndex(loops),
    snapIndex: buildSnapIndex(
      snapCandidates,
      diagonal > 0 ? diagonal * SNAP_CELL_FRACTION : 1,
    ),
  };
};

/**
 * Bounds of the layers that are currently shown.
 *
 * Returns the full extents when every layer is hidden, so fitting an entirely
 * hidden drawing still frames the raw geometry rather than collapsing the view.
 */
export const visibleBounds = (
  model: DrawingModel | null,
  hiddenLayers: ReadonlySet<number>,
): Aabb2 | null => {
  if (!model) {
    return null;
  }
  if (hiddenLayers.size === 0) {
    return model.bounds;
  }

  let bounds: Aabb2 | null = null;
  model.boundsByLayer.forEach((layerBounds, layerIndex) => {
    if (!hiddenLayers.has(layerIndex)) {
      bounds = unionAabb(bounds, layerBounds);
    }
  });

  return bounds ?? model.bounds;
};
